import type { Ref } from 'react';
import { forwardRef } from 'react';
import type React from 'react';

interface ScrollAreaProps {
  children: React.ReactNode;
  className?: string;
  maxHeight?: string;
  variant?: 'light' | 'dark';
  padding?: boolean;
  /** FE-003: forwarded to the scrollable div's `aria-busy` — set while the
   * caller is appending more content (e.g. an infinite-scroll fetch), per
   * frontend-accessibility's aria-dynamic-state rule. Omitted (not `false`)
   * when unset, so existing callers render no `aria-busy` attribute at all. */
  ariaBusy?: boolean;
}

/**
 * ScrollArea component provides consistent scrollbar styling across the application.
 *
 * Forwards its ref to the internal scrollable div so callers can use it as an
 * IntersectionObserver root (e.g. useLoadMoreSentinel) or otherwise reach the
 * actual scroll container element.
 *
 * @param {React.ReactNode} children - Content to be scrollable
 * @param {string} className - Additional class names
 * @param {string} maxHeight - Optional max height constraint
 * @param {string} variant - Reserved for a future forced-contrast override;
 *   currently unused (see scrollbar thumb color comment below)
 * @param {boolean} padding - Whether to add padding to the scroll area (defaults to true)
 * @param {boolean} ariaBusy - Forwarded to `aria-busy`; omitted entirely when unset
 */
export const ScrollArea = forwardRef(
  (
    { ariaBusy, children, className = '', maxHeight, padding = true }: ScrollAreaProps,
    ref: Ref<HTMLDivElement> | undefined,
  ) => {
    const style = maxHeight ? { maxHeight } : {};

    const paddingClasses = padding ? 'pr-3 pb-4' : '';

    // Thumb color follows the --color-border theme token (design-system-consistency's
    // theme-tokens-only rule) instead of hardcoded gray-300/gray-600 Tailwind classes.
    // --color-border itself already resolves to a different value in light vs dark
    // mode (packages/design-tokens/src/theme.css), so this single class covers both
    // themes without a separate dark: variant. The former light/dark `variant` prop
    // forced a *fixed* gray regardless of the active theme; no caller uses it today,
    // and hardcoding fixed values for it here would reintroduce the non-token colors
    // this change removes, so it is currently unused rather than reimplemented with
    // literal colors.
    return (
      <div
        ref={ref}
        aria-busy={ariaBusy}
        className={`overflow-y-auto ${paddingClasses} scrollbar-thin scrollbar-thumb-[var(--color-border)] scrollbar-track-transparent ${className}`}
        style={style}
      >
        {children}
      </div>
    );
  },
);

ScrollArea.displayName = 'ScrollArea';
